import { useState } from 'react'
import { Check, Download, FileSpreadsheet, Gift, Plus, ShieldCheck, Pencil } from 'lucide-react'
import { foutTekst, vraag } from '../lib/api'
import { bewaar, type Kolom, type Waarde } from '../lib/export'
import { datum, datumTijd, getal } from '../lib/opmaak'
import { alleVestigingen, euro, mag } from '../../supabase/functions/_gedeeld/regels.ts'
import { Lade } from '../onderdelen/Lade'
import { AfbeeldingKiezen } from './AfbeeldingKiezen'
import { Laden, useLaad, type Sessie } from './Beheer'

/* ------------------------------------------------------------------ *
 *  Beloningen
 * ------------------------------------------------------------------ */

interface BeloningRij {
  id: string; naam: string; punten: number; actief: boolean; alle: boolean; vestigingen: string[]; afbeelding: string | null
  type: 'fysiek' | 'digitaal'; voorraad: number | null
}

export function Beloningen({ s }: { s: Sessie }) {
  const { data, fout, laad, zet } = useLaad<{ beloningen: BeloningRij[] }>('beloningen')
  const openbaar = useLaad<{ regels: { waardePerPuntEuro: number } }>('vestigingen')
  const [bewerk, zetBewerk] = useState<Partial<BeloningRij> | null>(null)
  const waarde = openbaar.data?.regels.waardePerPuntEuro ?? 0
  return (
    <>
      <div className="pagina-kop">
        <h2>Beloningen</h2>
        <button className="knop geel" onClick={() => zetBewerk({ actief: false, alle: true, vestigingen: [] })}><Plus size={19} />Nieuwe beloning</button>
      </div>
      <p className="stil" style={{ marginBottom: 14 }}>Alleen actieve beloningen staan op de tablet en bij de klant, en alleen op de vestigingen waar ze gelden. €10 en €20 staan als voorbeeld klaar, maar zijn nog uitgeschakeld.</p>
      {!data ? <Laden fout={fout} opnieuw={laad} /> : (
        <div className="kaart" style={{ padding: '4px 0' }}>
          <table className="tabel kaartjes">
            <thead><tr><th>Beloning</th><th className="getal">Punten</th><th className="getal">Waarde</th><th>Soort</th><th className="getal">Voorraad</th><th>Geldt op</th><th>Status</th><th /></tr></thead>
            <tbody>
              {data.beloningen.map((b) => (
                <tr key={b.id} className="klikbaar" onClick={() => zetBewerk(b)}>
                  <td className="hoofd">
                    <span className="rij" style={{ flexWrap: 'nowrap' }}>
                      {b.afbeelding ? <img className="beloning-foto klein" src={b.afbeelding} alt="" /> : <Gift size={18} className="stil" />}
                      <span>{b.naam}</span>
                    </span>
                  </td>
                  <td className="getal" data-l="Punten">{b.punten}</td>
                  <td className="getal" data-l="Waarde">{waarde > 0 ? euro(b.punten * waarde) : '—'}</td>
                  <td data-l="Soort">{b.type === 'digitaal' ? 'Digitaal' : 'Fysiek'}</td>
                  <td className="getal" data-l="Voorraad">{b.voorraad === null ? <span className="stil">onbeperkt</span> : b.voorraad === 0 ? <span className="badge dicht">Uitverkocht</span> : b.voorraad}</td>
                  <td data-l="Geldt op" className="geen-breuk">{b.alle ? 'Alle vestigingen' : b.vestigingen.length === 1
                    ? s.vestigingen.find((v) => v.id === b.vestigingen[0])?.naam.replace(/^Truckwash\s*/i, '') ?? '1 vestiging'
                    : `${b.vestigingen.length} vestigingen`}</td>
                  <td data-l="Status">{b.actief ? <span className="badge open">Actief</span> : <span className="badge">Uit</span>}</td>
                  <td data-l="" className="verberg-mobiel"><Pencil size={17} className="stil" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Lade titel={bewerk?.id ? bewerk.naam ?? '' : 'Nieuwe beloning'} open={!!bewerk} sluit={() => zetBewerk(null)} breed
        kinderen={bewerk && <BeloningFormulier b={bewerk} s={s} klaar={(r) => { zet(r); zetBewerk(null) }}
          bijgewerkt={(r) => { zet(r); zetBewerk(r.beloningen.find((x) => x.id === bewerk.id) ?? bewerk) }} />} />
    </>
  )
}

function BeloningFormulier({ b, s, klaar, bijgewerkt }: {
  b: Partial<BeloningRij>; s: Sessie; klaar: (r: { beloningen: BeloningRij[] }) => void; bijgewerkt: (r: { beloningen: BeloningRij[] }) => void
}) {
  const [naam, zetNaam] = useState(b.naam ?? '')
  const [pt, zetPt] = useState(String(b.punten ?? ''))
  const [actief, zetActief] = useState(!!b.actief)
  const [alle, zetAlle] = useState(b.alle !== false)
  const [type, zetType] = useState<'fysiek' | 'digitaal'>(b.type ?? 'fysiek')
  const [voorraad, zetVoorraad] = useState(b.voorraad === null || b.voorraad === undefined ? '' : String(b.voorraad))
  const [vest, zetVest] = useState<string[]>(b.vestigingen ?? [])
  const [fout, zetFout] = useState<string | null>(null)
  const actieveVest = s.vestigingen.filter((v) => v.actief || vest.includes(v.id))
  return (
    <form className="formulier" onSubmit={async (e) => {
      e.preventDefault(); zetFout(null)
      try { klaar(await vraag('beloning-opslaan', { id: b.id, naam, punten: Number(pt), actief, alle, vestigingen: alle ? [] : vest, type, voorraad: voorraad === '' ? null : Number(voorraad) })) } catch (x) { zetFout(foutTekst(x)) }
    }}>
      <div className="twee-kol">
        <label className="veld"><span>Naam</span><input value={naam} onChange={(e) => zetNaam(e.target.value)} placeholder="€5 korting" required /></label>
        <label className="veld"><span>Punten</span><input value={pt} onChange={(e) => zetPt(e.target.value.replace(/\D/g, ''))} inputMode="numeric" required /></label>
        <label className="veld"><span>Soort</span>
          <select value={type} onChange={(e) => zetType(e.target.value as 'fysiek' | 'digitaal')}>
            <option value="fysiek">Fysiek (aan de balie)</option><option value="digitaal">Digitaal (code of tegoed)</option>
          </select>
        </label>
        <label className="veld"><span>Voorraad</span><input value={voorraad} onChange={(e) => zetVoorraad(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="leeg = onbeperkt" />
          <small>Loopt af bij elk inwisselen; op 0 kan hij niet meer.</small></label>
      </div>
      <div className="veld">
        <span>Geldt op</span>
        <div className="chips">
          <button type="button" className="chip" aria-pressed={alle} onClick={() => zetAlle(true)}>Alle vestigingen</button>
          <button type="button" className="chip" aria-pressed={!alle} onClick={() => zetAlle(false)}>Kies vestigingen</button>
        </div>
        {alle
          ? <small>Ook vestigingen die er later bij komen.</small>
          : <>
            <div className="rij" style={{ margin: '2px 0 6px' }}>
              <button type="button" className="knop klein-knop" onClick={() => zetVest(actieveVest.map((v) => v.id))}>Alles aanvinken</button>
              <button type="button" className="knop klein-knop stil-knop" onClick={() => zetVest([])}>Niets</button>
              <span className="stil klein">{vest.length} gekozen</span>
            </div>
            <div className="keuzes">
              {actieveVest.map((v) => (
                <label key={v.id} className="keuze">
                  <input type="checkbox" checked={vest.includes(v.id)} onChange={(e) => zetVest(e.target.checked ? [...vest, v.id] : vest.filter((x) => x !== v.id))} />
                  {v.naam.replace(/^Truckwash\s*/i, '')}
                </label>
              ))}
            </div>
          </>}
      </div>
      <div className="veld">
        <span>Afbeelding</span>
        {b.id ? <AfbeeldingKiezen actie="beloning-afbeelding" id={b.id} url={b.afbeelding ?? null} bijgewerkt={bijgewerkt} /> : <small>Sla de beloning eerst op; daarna kun je er een foto bij zetten.</small>}
      </div>
      <label className="vink"><input type="checkbox" checked={actief} onChange={(e) => zetActief(e.target.checked)} /><span>Actief: zichtbaar en in te wisselen</span></label>
      {fout && <div className="melding fout">{fout}</div>}
      <div className="knoppenrij"><button className="knop geel">Opslaan</button></div>
    </form>
  )
}

/* ------------------------------------------------------------------ *
 *  Chauffeursbeloningen
 * ------------------------------------------------------------------ */

interface ChauffeurRij { id: string; t: string; vestiging: string; bezocht: number; totaal: number; afgehandeld: boolean; email: string; naam: string; codes: string[] }

export function Chauffeurs() {
  const { data, fout, laad, zet } = useLaad<{ chauffeurs: ChauffeurRij[] }>('chauffeurs')
  const afhandelen = async (c: ChauffeurRij) => {
    try { zet(await vraag('chauffeur-afhandelen', { id: c.id, afgehandeld: !c.afgehandeld })) } catch (e) { alert(foutTekst(e)) }
  }
  return (
    <>
      <div className="pagina-kop"><h2>Chauffeursbeloningen</h2></div>
      <p className="stil" style={{ marginBottom: 14 }}>Chauffeurs die genoeg verschillende vestigingen bezochten binnen 12 maanden. Welke beloning ze krijgen spreken jullie per geval af; vink af als het geregeld is.</p>
      {!data ? <Laden fout={fout} opnieuw={laad} /> : data.chauffeurs.length === 0 ? <div className="leeg-staat">Nog niemand.</div> : (
        <div className="kaart" style={{ padding: '4px 0' }}>
          <table className="tabel kaartjes">
            <thead><tr><th>Kaart</th><th>Gehaald</th><th>Op</th><th className="getal">Vestigingen</th><th>Contact</th><th /></tr></thead>
            <tbody>
              {data.chauffeurs.map((c) => (
                <tr key={c.id}>
                  <td className="hoofd code-pil">{c.codes.join(' · ')}</td>
                  <td data-l="Gehaald">{datum(c.t)}</td>
                  <td data-l="Op">{c.vestiging}</td>
                  <td className="getal" data-l="Vestigingen">{c.bezocht}/{c.totaal}</td>
                  <td data-l="Contact">{c.email || <span className="stil">geen e-mail</span>}</td>
                  <td data-l="">
                    <button className={`knop klein-knop ${c.afgehandeld ? '' : 'geel'}`} onClick={() => afhandelen(c)}>
                      {c.afgehandeld ? <><Check size={16} />Afgehandeld</> : 'Afhandelen'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

/* ------------------------------------------------------------------ *
 *  Export
 * ------------------------------------------------------------------ */

const vandaag = () => new Date().toISOString().slice(0, 10)
const dagenTerug = (n: number) => new Date(Date.now() - n * 86400_000).toISOString().slice(0, 10)

export function Exporteren({ s }: { s: Sessie }) {
  const soorten = [
    { k: 'marketing', naam: 'Marketing', uitleg: 'Alleen klanten met toestemming voor mail: e-mail, naam, punten.', recht: 'export-marketing' as const },
    { k: 'scans', naam: 'Scans', uitleg: 'Elke wasbeurt met tag en vestiging.', recht: 'export-activiteit' as const },
    { k: 'inwisselingen', naam: 'Inwisselingen', uitleg: 'Elke afschrijving met beloning en medewerker.', recht: 'export-activiteit' as const },
    { k: 'klanten', naam: 'Alle klanten', uitleg: 'Iedereen, met tags, saldo en toestemming.', recht: 'export-klanten' as const },
  ].filter((x) => mag(s.ik.rol, x.recht))
  const [soort, zetSoort] = useState(soorten[0]?.k ?? '')
  const [van, zetVan] = useState(dagenTerug(30))
  const [tot, zetTot] = useState(vandaag())
  const [vest, zetVest] = useState<string[]>([])
  const [bezig, zetBezig] = useState(false)
  const [uit, zetUit] = useState<string | null>(null)
  const [fout, zetFout] = useState<string | null>(null)
  const periode = soort === 'scans' || soort === 'inwisselingen'
  const mijn = s.vestigingen.filter((v) => alleVestigingen(s.ik.rol) || s.ik.vestigingen.includes(v.id))

  const exporteer = async (formaat: 'csv' | 'xlsx') => {
    zetBezig(true); zetFout(null); zetUit(null)
    try {
      const r = await vraag<{ kolommen: Kolom[]; rijen: Waarde[][]; afgekapt: boolean }>('export', { soort, formaat, van, tot, vestigingen: vest })
      const naam = soorten.find((x) => x.k === soort)?.naam ?? soort
      bewaar(`TW1 Club ${naam}${periode ? ` ${van} tm ${tot}` : ''}`, formaat, r.kolommen, r.rijen)
      zetUit(`${getal(r.rijen.length)} rijen geëxporteerd${r.afgekapt ? ' (afgekapt op 100.000)' : ''}. Deze export staat in het exportlog.`)
    } catch (e) { zetFout(foutTekst(e)) } finally { zetBezig(false) }
  }

  return (
    <>
      <div className="pagina-kop"><h2>Export</h2></div>
      <div className="lijst" style={{ maxWidth: 720 }}>
        <div className="keuze-tegels">
          {soorten.map((x) => (
            <label key={x.k} className="keuze-tegel" style={{ borderColor: soort === x.k ? 'var(--line-brand)' : undefined, background: soort === x.k ? 'var(--tint-brand)' : undefined }}>
              <input type="radio" name="soort" checked={soort === x.k} onChange={() => zetSoort(x.k)} style={{ width: 20, height: 20, accentColor: 'var(--brand)' }} />
              <span><b>{x.naam}</b><small>{x.uitleg}</small></span>
            </label>
          ))}
        </div>
        {periode && (
          <section className="kaart vol formulier">
            <div className="twee-kol">
              <label className="veld"><span>Van</span><input type="date" value={van} onChange={(e) => zetVan(e.target.value)} /></label>
              <label className="veld"><span>Tot en met</span><input type="date" value={tot} onChange={(e) => zetTot(e.target.value)} /></label>
            </div>
            <div className="chips">
              {[['7 dagen', 7], ['30 dagen', 30], ['90 dagen', 90], ['Jaar', 365]].map(([n, d]) => (
                <button key={n} type="button" className="chip" onClick={() => { zetVan(dagenTerug(Number(d))); zetTot(vandaag()) }}>{n}</button>
              ))}
            </div>
            <div className="veld"><span>Vestigingen {vest.length ? `(${vest.length})` : '(alle)'}</span>
              <div className="keuzes">
                {mijn.map((v) => (
                  <label key={v.id} className="keuze">
                    <input type="checkbox" checked={vest.includes(v.id)} onChange={(e) => zetVest(e.target.checked ? [...vest, v.id] : vest.filter((x) => x !== v.id))} />
                    {v.naam.replace(/^Truckwash\s*/i, '')}
                  </label>
                ))}
              </div>
            </div>
          </section>
        )}
        {soort === 'marketing' && <div className="melding info"><ShieldCheck size={19} /><span>Alleen klanten met toestemming. Gebruik dit bestand alleen voor mailings van Truckwash 1 Group en bewaar het niet langer dan nodig.</span></div>}
        {fout && <div className="melding fout">{fout}</div>}
        {uit && <div className="melding ok">{uit}</div>}
        <div className="route-knoppen">
          <button className="knop geel" disabled={bezig || !soort} onClick={() => exporteer('xlsx')}><FileSpreadsheet size={19} />Excel</button>
          <button className="knop" disabled={bezig || !soort} onClick={() => exporteer('csv')}><Download size={19} />CSV</button>
        </div>
      </div>
    </>
  )
}

/* ------------------------------------------------------------------ *
 *  Instellingen en het exportlog
 * ------------------------------------------------------------------ */

interface RegelsData { puntenPerWasbeurt: number; waardePerPuntEuro: number; cooldownMinuten: number; pinLengte: number; maxFoutePogingen: number; chauffeurDrempelProcent: number; chauffeurVensterMaanden: number }

export function Instellingen() {
  const { data, fout, laad, zet } = useLaad<{ regels: RegelsData }>('instellingen')
  const [procent, zetProcent] = useState<number | null>(null)
  const [melding, zetMelding] = useState<string | null>(null)
  if (!data) return <><div className="pagina-kop"><h2>Instellingen</h2></div><Laden fout={fout} opnieuw={laad} /></>
  const r = data.regels
  const p = procent ?? r.chauffeurDrempelProcent
  const rijen: [string, string][] = [
    ['Punten per wasbeurt', String(r.puntenPerWasbeurt)],
    ['Waarde per punt', euro(r.waardePerPuntEuro)],
    ['Wachttijd op dezelfde vestiging', `${r.cooldownMinuten} minuten`],
    ['PIN', `${r.pinLengte} cijfers, ${r.maxFoutePogingen} foute pogingen blokkeert`],
    ['Periode chauffeursbeloning', `${r.chauffeurVensterMaanden} maanden`],
  ]
  return (
    <>
      <div className="pagina-kop"><h2>Instellingen</h2></div>
      <div className="lijst" style={{ maxWidth: 720 }}>
        <section className="kaart vol formulier">
          <b>Drempel chauffeursbeloning</b>
          <div className="chips">
            {[75, 100].map((x) => <button key={x} className="chip" aria-pressed={p === x} onClick={() => zetProcent(x)}>{x}%</button>)}
          </div>
          <label className="veld"><span>Of zelf (procent van de actieve vestigingen)</span>
            <span className="rij" style={{ flexWrap: 'nowrap' }}><input inputMode="numeric" value={p} style={{ maxWidth: 110 }} onChange={(e) => zetProcent(Number(e.target.value.replace(/\D/g, '')) || 0)} /> %</span>
          </label>
          {melding && <div className="melding ok">{melding}</div>}
          <div className="knoppenrij">
            <button className="knop geel" disabled={p === r.chauffeurDrempelProcent} onClick={async () => {
              try { zet(await vraag('instellingen-opslaan', { chauffeurDrempelProcent: p })); zetProcent(null); zetMelding('Opgeslagen.') } catch (e) { zetMelding(foutTekst(e)) }
            }}>Opslaan</button>
          </div>
        </section>
        <section className="kaart vol">
          <b>Vaste regels</b>
          <p className="stil klein" style={{ margin: '4px 0 8px' }}>Dit zijn de vaste spelregels van TW1 Club; wijzigen gaat via overleg met MT.</p>
          <table className="uren-tabel"><tbody>{rijen.map(([a, b]) => <tr key={a}><td>{a}</td><td>{b}</td></tr>)}</tbody></table>
        </section>
      </div>
    </>
  )
}

interface LogRij { t: string; naam: string; rol: string; soort: string; formaat: string; aantal: number; filters: Record<string, unknown> }

const FILTERNAAM: Record<string, string> = { promotie: 'Voucher', doelgroep: 'Aan', vestiging: 'Vestiging', van: 'Van', tot: 'Tot', dagen: 'Periode' }
const SOORTNAAM: Record<string, string> = {
  marketing: 'Marketing', klanten: 'Alle klanten', scans: 'Wasbeurten', inwisselingen: 'Inwisselingen', 'vouchers-uitgedeeld': 'Vouchers uitgedeeld',
}

export function Exportlog() {
  const { data, fout, laad } = useLaad<{ log: LogRij[] }>('exportlog')
  return (
    <>
      <div className="pagina-kop"><h2>Exportlog</h2></div>
      {!data ? <Laden fout={fout} opnieuw={laad} /> : data.log.length === 0 ? <div className="leeg-staat">Nog geen exports.</div> : (
        <div className="kaart" style={{ padding: '4px 0' }}>
          <table className="tabel kaartjes">
            <thead><tr><th>Wanneer</th><th>Wie</th><th>Wat</th><th className="getal">Rijen</th><th>Filters</th></tr></thead>
            <tbody>
              {data.log.map((l, i) => (
                <tr key={i}>
                  <td className="hoofd">{datumTijd(l.t)}</td>
                  <td data-l="Wie">{l.naam}</td>
                  <td data-l="Wat">{SOORTNAAM[l.soort] ?? l.soort}{l.formaat && l.formaat !== '-' ? ` · ${l.formaat}` : ''}</td>
                  <td className="getal" data-l="Rijen">{getal(l.aantal)}</td>
                  <td data-l="Filters" className="stil klein">{Object.entries(l.filters).map(([k, v]) => `${FILTERNAAM[k] ?? k}: ${Array.isArray(v) ? v.join(', ') : String(v)}`).join(' · ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
